<template>
  <div class="ism-tbl-card">

    <!-- ── Card Header ── -->
    <div class="ism-tbl-header">
      <div>
        <div class="ism-card-title">{{ title }}</div>
        <div v-if="subtitle" class="ism-card-subtitle">{{ subtitle }}</div>
      </div>

      <div class="ism-tbl-header-actions">
        <!-- Search -->
        <div class="ism-search" v-if="showSearch">
          <v-icon size="16" color="#94a3b8">mdi-magnify</v-icon>
          <input
            v-model="localSearch"
            :placeholder="searchPlaceholder"
            @input="$emit('update:search', localSearch)"
          />
          <v-icon
            v-if="localSearch"
            size="14"
            color="#94a3b8"
            style="cursor:pointer"
            @click="clearSearch"
          >mdi-close</v-icon>
        </div>

        <!-- Extra filters slot -->
        <slot name="filters" />

        <!-- Add Button -->
        <button v-if="showAdd" class="btn-accent" @click="$emit('add')">
          <v-icon size="16" color="#fff">mdi-plus</v-icon>
          {{ addLabel }}
        </button>
      </div>
    </div>

    <!-- ── Table wrapper ── -->
    <div class="ism-tbl-wrapper">
      <table class="ism-table">

        <!-- thead -->
        <thead class="ism-thead">
          <tr>
            <th style="width: 50px">No.</th>
            <th
              v-for="h in headers"
              :key="h.value"
              :style="h.width ? { width: h.width } : {}"
            >{{ h.text }}</th>
          </tr>
        </thead>

        <!-- tbody: shimmer skeleton -->
        <tbody v-if="loading">
          <tr v-for="i in 6" :key="'sk' + i" class="ism-tr">
            <td>
              <div class="ism-shimmer" style="height: 12px; width: 24px; border-radius: 4px;"></div>
            </td>
            <td v-for="(h, j) in headers" :key="j">
              <div
                class="ism-shimmer"
                :style="{ height: '12px', width: j === 0 ? '36px' : '72%', borderRadius: '4px' }"
              ></div>
            </td>
          </tr>
        </tbody>

        <!-- tbody: empty state -->
        <tbody v-else-if="!loading && pagedItems.length === 0">
          <tr>
            <td :colspan="headers.length + 1" class="ism-empty-cell">
              <div class="ism-empty-state">
                <v-icon size="44" color="#cbd5e1">mdi-database-off-outline</v-icon>
                <p class="ism-empty-text">{{ noDataText }}</p>
              </div>
            </td>
          </tr>
        </tbody>

        <!-- tbody: data rows -->
        <tbody v-else>
          <tr
            v-for="(item, idx) in pagedItems"
            :key="idx"
            class="ism-tr"
          >
            <td>{{ firstRow + idx }}</td>
            <td v-for="h in headers" :key="h.value">
              <!-- scoped slot per column -->
              <slot :name="'item.' + h.value" :item="item">
                {{ item[h.value] }}
              </slot>
            </td>
          </tr>
        </tbody>

      </table>
    </div>

    <div class="ism-tbl-footer" v-if="!loading && totalItems > 0">
      <div class="ism-pag-container">
        <!-- Show rows -->
        <div class="ism-rpp-wrap">
          <span class="ism-rpp-label">Show rows</span>
          <v-menu offset-y nudge-bottom="4" content-class="ism-rpp-menu-pop">
            <template v-slot:activator="{ on, attrs }">
              <div class="ism-rpp-select" v-bind="attrs" v-on="on">
                <span>{{ itemsPerPage }}</span>
                <v-icon size="14" class="ism-rpp-arrow">mdi-chevron-down</v-icon>
              </div>
            </template>
            <div class="ism-rpp-list">
              <div
                v-for="opt in rowsPerPageOpts"
                :key="opt"
                class="ism-rpp-item"
                :class="{ 'ism-rpp-item--active': itemsPerPage === opt }"
                @click="itemsPerPage = opt; page = 1"
              >
                {{ opt }}
              </div>
            </div>
          </v-menu>
        </div>

        <!-- Row range -->
        <span class="ism-row-range">{{ firstRow }}&ndash;{{ lastRow }} of {{ totalItems }}</span>

        <!-- Pagination buttons -->
        <div class="ism-pag-btns">
          <button class="ism-pag-btn" :disabled="page === 1" @click="page = 1" title="First">
            <v-icon size="14">mdi-chevron-double-left</v-icon>
          </button>
          <button class="ism-pag-btn" :disabled="page === 1" @click="page--" title="Previous">
            <v-icon size="14">mdi-chevron-left</v-icon>
          </button>
          <button class="ism-pag-btn" :disabled="page === totalPages" @click="page++" title="Next">
            <v-icon size="14">mdi-chevron-right</v-icon>
          </button>
          <button class="ism-pag-btn" :disabled="page === totalPages" @click="page = totalPages" title="Last">
            <v-icon size="14">mdi-chevron-double-right</v-icon>
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'PremiumTable',

  props: {
    title:             { type: String,  default: '' },
    subtitle:          { type: String,  default: '' },
    headers:           { type: Array,   required: true },
    items:             { type: Array,   default: () => [] },
    loading:           { type: Boolean, default: false },
    search:            { type: String,  default: '' },
    searchPlaceholder: { type: String,  default: 'Search...' },
    showSearch:        { type: Boolean, default: true },
    showAdd:           { type: Boolean, default: false },
    addLabel:          { type: String,  default: 'Add' },
    noDataText:        { type: String,  default: 'No data available' },
    rowsPerPageOpts:   { type: Array,   default: () => [10, 25, 50, 100] },
  },

  emits: ['update:search', 'add'],

  data() {
    return {
      localSearch: this.search,
      page: 1,
      itemsPerPage: 10,
    };
  },

  computed: {
    filteredItems() {
      if (!this.localSearch) return this.items;
      const q = this.localSearch.toLowerCase();

      // Deep search function to check nested objects
      const deepSearch = (obj) => {
        if (!obj) return false;
        return Object.values(obj).some(v => {
          if (v && typeof v === 'object') return deepSearch(v);
          return String(v).toLowerCase().includes(q);
        });
      };

      return this.items.filter(item => deepSearch(item));
    },
    totalItems()  { return this.filteredItems.length; },
    totalPages()  { return Math.max(1, Math.ceil(this.totalItems / this.itemsPerPage)); },
    firstRow()    { return this.totalItems === 0 ? 0 : (this.page - 1) * this.itemsPerPage + 1; },
    lastRow()     { return Math.min(this.page * this.itemsPerPage, this.totalItems); },
    pagedItems() {
      const start = (this.page - 1) * this.itemsPerPage;
      return this.filteredItems.slice(start, start + this.itemsPerPage);
    },
  },

  methods: {
    clearSearch() {
      this.localSearch = '';
      this.page = 1;
      this.$emit('update:search', '');
    },
  },

  watch: {
    search(val)    { this.localSearch = val; },
    localSearch()  { this.page = 1; },
    totalPages(v)  { if (this.page > v) this.page = v; },
  },
};
</script>

<style scoped>
/* ── Card wrapper ─────────────────────────── */
.ism-tbl-card {
  background: var(--card-bg, #fff);
  border: 1px solid var(--border-color, rgba(0,0,0,0.07));
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--shadow-sm, 0 1px 4px rgba(0,0,0,0.06));
}

/* ── Header ───────────────────────────────── */
.ism-tbl-header {
  padding: 18px 24px;
  border-bottom: 1px solid var(--border-color, rgba(0,0,0,0.06));
  background: var(--card-bg, #fff);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.ism-card-title {
  font-size: 15px;
  font-weight: 800;
  color: var(--text-primary, #1e293b);
  font-family: "Inter", sans-serif;
  letter-spacing: -0.02em;
}

.ism-card-subtitle {
  font-size: 12px;
  color: var(--text-secondary, #64748b);
  margin-top: 2px;
  font-family: "Inter", sans-serif;
}

.ism-tbl-header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

/* ── Search ───────────────────────────────── */
.ism-search {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--bg-secondary, #fff);
  border: 1px solid var(--border-color, rgba(0,0,0,0.1));
  border-radius: 8px;
  padding: 8px 14px;
  min-width: 240px;
  height: 40px;
  transition: all 0.2s;
}
.ism-search:focus-within {
  border-color: var(--accent, #F26522);
  box-shadow: 0 0 0 3px var(--accent-lt, rgba(242,101,34,0.08));
}
.ism-search input {
  border: none; outline: none; background: transparent;
  color: var(--text-primary, #1e293b);
  font-family: "Inter", sans-serif;
  font-size: 13px;
  width: 100%;
}
.ism-search input::placeholder { color: #94a3b8; }

/* ── Add button ───────────────────────────── */
.btn-accent {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #F26522, #C94F10);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(242,101,34,0.25);
  white-space: nowrap;
}
.btn-accent:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(242,101,34,0.35); }

/* ── Table wrapper ────────────────────────── */
.ism-tbl-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* ── Table ────────────────────────────────── */
.ism-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-family: "Inter", sans-serif;
}

/* ── Thead ────────────────────────────────── */
.ism-thead {
  position: sticky;
  top: 0;
  z-index: 10;
  background: var(--bg-primary, #f5f6f7);
  box-shadow: 0 1px 0 var(--border-color, rgba(0,0,0,0.07));
}

.ism-thead th {
  height: 44px;
  font-size: 13px;
  font-weight: 700;
  text-transform: capitalize;
  letter-spacing: 0.4px;
  color: var(--text-secondary, #64748b);
  border-bottom: 1px solid var(--border-color, rgba(0,0,0,0.07));
  padding: 10px 14px;
  white-space: nowrap;
  text-align: left;
}

.ism-thead th:first-child { padding-left: 24px; }

/* ── Tbody rows ───────────────────────────── */
.ism-tr {
  transition: background 0.15s ease;
}

.ism-table tbody tr td {
  height: 52px;
  font-size: 13.5px;
  color: var(--text-primary, #1e293b);
  padding: 5px 14px;
  border-bottom: 1px solid var(--border-color, rgba(0,0,0,0.07));
  vertical-align: middle;
  background: var(--card-bg, #fff);
  transition: background 0.15s ease;
  white-space: nowrap;
}

.ism-table tbody tr td:first-child { padding-left: 24px; }

.ism-table tbody tr:hover td {
  background: var(--accent-lt, rgba(242,101,34,0.05)) !important;
}

.ism-table tbody tr:last-child td {
  border-bottom: none;
}

/* ── Shimmer skeleton ─────────────────────── */
.ism-shimmer {
  background: linear-gradient(
    90deg,
    var(--bg-primary, #f1f5f9) 25%,
    var(--border-color, rgba(0,0,0,0.07)) 50%,
    var(--bg-primary, #f1f5f9) 75%
  );
  background-size: 200% 100%;
  animation: ism-shimmer-anim 1.5s infinite linear;
  border-radius: 4px;
}

@keyframes ism-shimmer-anim {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Empty state ──────────────────────────── */
.ism-empty-cell { border-bottom: none !important; }
.ism-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 48px 24px;
}
.ism-empty-text {
  font-size: 14px;
  color: var(--text-secondary, #64748b);
  font-family: "Inter", sans-serif;
  margin: 0;
}

/* ── Footer ───────────────────────────────── */
.ism-tbl-footer {
  padding: 16px 24px;
  border-top: 1px solid var(--border-color, rgba(0,0,0,0.07));
  background: transparent;
  display: flex;
  justify-content: flex-end;
}

.ism-pag-container {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}

/* Show rows */
.ism-rpp-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ism-rpp-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary, #64748b);
  white-space: nowrap;
}

.ism-rpp-select {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  width: 65px;
  background: var(--bg-primary, transparent);
  border: 1px solid var(--border-color, rgba(0,0,0,0.1));
  border-radius: 8px;
  color: var(--text-primary, #1e293b);
  font-size: 13px;
  font-weight: 600;
  padding: 6px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.dark-mode .ism-rpp-select {
  background: rgba(0, 0, 0, 0.2);
}

.ism-rpp-select:hover {
  border-color: var(--accent, #F26522);
}

.ism-rpp-arrow {
  color: var(--text-secondary, #64748b) !important;
}

.ism-row-range {
  font-size: 12.5px;
  color: var(--text-secondary, #64748b);
  font-weight: 600;
  white-space: nowrap;
}

/* Custom Dropdown Menu */
.ism-rpp-menu-pop {
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
  border: 1px solid var(--border-color, rgba(0,0,0,0.1)) !important;
  overflow: hidden;
}
.dark-mode .ism-rpp-menu-pop {
  box-shadow: 0 4px 12px rgba(0,0,0,0.3) !important;
}

.ism-rpp-list {
  background: var(--card-bg, #fff);
  display: flex;
  flex-direction: column;
  min-width: 65px;
}

.ism-rpp-item {
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary, #1e293b);
  cursor: pointer;
  transition: background 0.2s;
  text-align: center;
}

.ism-rpp-item:hover {
  background: var(--bg-primary, #f1f5f9);
}

.ism-rpp-item--active {
  background: rgba(242, 101, 34, 0.1);
  color: var(--accent, #F26522);
  font-weight: 700;
}

/* Pagination buttons */
.ism-pag-btns {
  display: flex;
  align-items: center;
  gap: 4px;
}

.ism-pag-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: var(--text-secondary, #64748b);
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ism-pag-btn:hover:not(:disabled) {
  background: rgba(100, 116, 139, 0.15);
  color: var(--text-primary, #1e293b);
}

.dark-mode .ism-pag-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.ism-pag-btn:disabled {
  opacity: 0.25;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .ism-tbl-header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 14px 16px;
  }
  .ism-tbl-header-actions {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }
  .ism-search {
    min-width: 0 !important;
    width: 100% !important;
  }
  .btn-accent {
    width: 100%;
    justify-content: center;
    min-height: 44px;
  }
}

@media (max-width: 600px) {
  .ism-tbl-header { padding: 14px 16px; }
  .ism-tbl-footer { justify-content: center; padding: 16px; }
  .ism-pag-container { gap: 16px; justify-content: center; }
}
@media (max-width: 480px) {
  .ism-tbl-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 12px;
  }
  .ism-tbl-header-actions {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  .ism-search {
    min-width: 0 !important;
    width: 100% !important;
  }
  .btn-accent {
    width: 100% !important;
    justify-content: center;
  }
}
</style>
